import React from 'react';
import { Group, Title, Button, Box, Tooltip, Flex } from '@mantine/core';
import { createStyles } from '@mantine/styles';
import {
  IconBrain,
  IconInfoCircle,
  IconSun,
  IconMoon,
} from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
  header: {
    background:
      'linear-gradient(90deg, var(--primary-color), var(--secondary-color))',
    borderBottom: 0,
    boxShadow: 'var(--shadow-md)',
    height: '70px',
    display: 'flex',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%',
    backdropFilter: 'blur(8px)',
    '[data-theme="dark"] &': {
      background: 'linear-gradient(90deg, #1e3a8a, #164e63)',
      boxShadow: 'var(--shadow-lg)',
    },
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '100%',
    padding: '0 var(--spacing-md)',
    paddingLeft: '10px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  leftContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    padding: 0,
    marginLeft: '-10px',
  },
  title: {
    color: 'white',
    fontWeight: 700,
    fontSize: '1.5rem',
    margin: 0,
    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
    letterSpacing: '0.5px',
    '@media (max-width: 576px)': {
      fontSize: '1.2rem',
    },
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    cursor: 'pointer',
    transition: 'var(--transition)',
    padding: 0,
    margin: 0,
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  button: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    backdropFilter: 'blur(4px)',
    color: 'white',
    fontWeight: 500,
    transition: 'var(--transition)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    },
  },
  iconButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    backdropFilter: 'blur(4px)',
    color: 'white',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    padding: 0,
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      transform: 'translateY(-2px) scale(1.05)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  brainIcon: {
    color: 'white',
    filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.2))',
    animation: 'pulse 3s infinite ease-in-out',
    transition: 'var(--transition)',
    marginRight: '10px',
    '@media (max-width: 576px)': {
      marginRight: '6px',
    },
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
  },
}));

const Navbar = ({ onResetModelSelection, colorScheme, toggleColorScheme }) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.header}>
      <div className={classes.container}>
        <div className={classes.leftContainer}>
          <Group
            className={classes.logo}
            spacing="xs"
            onClick={onResetModelSelection}
            role="button"
            aria-label="Go to model selection"
            position="left"
            noWrap
          >
            <IconBrain size={32} className={classes.brainIcon} stroke={1.5} />
            <Title order={2} className={classes.title}>
              Face Age Detector
            </Title>
          </Group>
        </div>
        <div className={classes.buttonsContainer}>
          <Tooltip
            label={colorScheme === 'dark' ? 'Light mode' : 'Dark mode'}
            position="bottom"
            withArrow
          >
            <Button
              className={classes.iconButton}
              variant="filled"
              onClick={toggleColorScheme}
              p={0}
              radius="xl"
            >
              {colorScheme === 'dark' ? (
                <IconSun size={20} stroke={1.5} />
              ) : (
                <IconMoon size={20} stroke={1.5} />
              )}
            </Button>
          </Tooltip>
          <Tooltip label="About" position="bottom" withArrow>
            <Button
              className={classes.iconButton}
              variant="filled"
              component="a"
              href="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
              target="_blank"
              p={0}
              radius="xl"
            >
              <IconInfoCircle size={20} stroke={1.5} />
            </Button>
          </Tooltip>
        </div>
      </div>
    </Box>
  );
};

export default Navbar;
